/**
 * Blog posts — single source of truth for /blog list page,
 * individual post pages, JSON-LD BlogPosting / Blog schemas,
 * and RSS/Atom feed generation in scripts/prerender.mjs.
 */

export type BlogPostBlock =
  | { kind: "p"; text: string }
  | { kind: "h2"; text: string; lead?: string }
  | { kind: "h3"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; items: string[] }
  | { kind: "quote"; text: string; cite?: string }
  | { kind: "callout"; tone: "info" | "warn" | "success"; title: string; body: string };

export type BlogPost = {
  /** URL slug, used for /blog/<slug>/ route. */
  slug: string;
  /** Full canonical path (with trailing slash). */
  path: `/blog/${string}/`;
  /** ISO 8601 publish date (yyyy-mm-dd). */
  date: string;
  /** Author canonical name (matches Organization in JSON-LD). */
  author: string;
  /** ≤ 70 chars Page <title>. */
  title: string;
  /** ≤ 160 chars meta description, doubles as RSS feed `summary`. */
  description: string;
  /** 60–120 char self-contained TL;DR; used as first paragraph + LLM summary. */
  tldr: string;
  /** OG image path (default `/og/blog-default.png` if missing). */
  ogImagePath?: string;
  /** Topic tags, used for grouping & in-content cross-linking. */
  tags: string[];
  /** Body blocks, rendered in order. */
  body: BlogPostBlock[];
  /** Recommended next articles or pages. */
  nextLinks: Array<{ href: string; label: string }>;
};

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: "2026-04-opc-and-hotline",
    path: "/blog/2026-04-opc-and-hotline/",
    date: "2026-04-23",
    author: "CALL ANYTHING",
    title: "OPC 经济与 Hotline 协议：超级个体的数字分身",
    description:
      "为什么超级个体（OPC）值得拥有自己的数字分身？一根 Hotline 如何把私域专长变成 Agent 7×24 可调用、可结算的产品形态。",
    tldr:
      "CALL ANYTHING 把超级个体（OPC）作为第一受众。OPC 用 Hotline 把私域专长包装成可被任何 Agent 发现、调用、按次结算的协议化产品；这种「数字分身」不是 AI 头像，而是 7×24 在线、契约清晰、收入入账的对外服务实体。",
    ogImagePath: "/og/blog-opc-and-hotline.png",
    tags: ["opc", "hotline", "narrative"],
    body: [
      {
        kind: "p",
        text: "「超级个体」这个词最近被反复使用，但绝大多数讨论都停留在「一个人用 AI 做更多事」。这种叙事真正缺的是协议——把个体的专长真正变成一种可以被 Agent 自动发现、自动调用、自动结算的产品形态。CALL ANYTHING 想填的就是这个空缺，我们把它叫做 OPC（One-Person Company）。",
      },
      {
        kind: "h2",
        text: "OPC 不是独立开发者，也不是自由职业",
        lead: "三者表面相似，背后是不同的对外形态。",
      },
      {
        kind: "p",
        text: "独立开发者（indie hacker）的产品是软件——他要做 UI、做付费、做客服。自由职业（freelancer）的产品是时间——他出售自己每小时的注意力。OPC 的产品是协议化能力——他不卖软件、不卖时间，他卖 Hotline。",
      },
      {
        kind: "ul",
        items: [
          "独立开发者：要建网站、做 onboarding、写文档、跑增长。",
          "自由职业：能力被时间封顶，难以复用、难以涨价。",
          "OPC：能力做成一根 Hotline，Marketplace 帮他做发现，Agent 帮他做调用，协议帮他做结算。",
        ],
      },
      {
        kind: "h2",
        text: "数字分身不是 AI 头像，是 7×24 的协议化营业实体",
      },
      {
        kind: "p",
        text: "市面上「数字分身」常被理解成一段会说话的视频或一个聊天机器人，把人的形象和声音留下来。CALL ANYTHING 的「数字分身」是另一种东西——它是 OPC 把自己的专业流程封装成 Hotline 后，跑在 supervisor 上的那段持续在线的服务实体。客户调用的是能力，不是声音；产生的是结果，不是对话。",
      },
      {
        kind: "callout",
        tone: "info",
        title: "对外暴露契约，对内保护资产",
        body: "Hotline 只对外暴露 input_schema / output_schema 与一段 summary，核心算法、Prompt、私有数据都留在 Responder 本机的 supervisor 进程里。这一条让 OPC 第一次可以「让模型用我的能力，但不让模型看到我的专长」。",
      },
      {
        kind: "h2",
        text: "为什么不是 MCP / Skill / API",
        lead: "前三者都是「接入面」，Hotline 是「产品本体」。",
      },
      {
        kind: "p",
        text: "MCP server 解决「Agent runtime 怎么 invoke 一个工具」，关心管道；Skill 通过 SKILL.md 把一段提示词注入 prompt，源码完全暴露、消耗 maxtoken、不带收益；API/SaaS 假设供给方是公司，要求 Caller 为每家写 client、签合同、按 plan 付费。三者都没回答「OPC 怎么把能力变成产品」。Hotline 把这件事原生做掉。",
      },
      {
        kind: "ul",
        items: [
          "对 MCP server：Hotline 是它的超集，可以同时暴露成 MCP server。",
          "对 Skill：Hotline 不进 prompt，可被 Skill 引导调用。",
          "对 API：Caller 不需要写 client，所有 Hotline 共享同一个 result_package。",
        ],
      },
      {
        kind: "h2",
        text: "OPC 的一天看起来是什么样",
      },
      {
        kind: "p",
        text: "想象一位经验丰富的 SQL 优化顾问。他过去要用 Notion 做案例库、用微信谈单、用支付宝收款、用 Cron 跑回访。今天他把「分析慢查询并给出索引建议」打成一根 Hotline，挂到 Marketplace。第二天起，全球任何有数据库的 Agent 项目都可以发现这根 Hotline 并直接调用，每次调用按 result_package 自动结算到他的账户。他真正花时间的，只剩下「让这根 Hotline 越来越好」这一件事。",
      },
      {
        kind: "callout",
        tone: "success",
        title: "5 分钟开通你的第一根 Hotline",
        body: "Quick Start for Responders 演示了完整链路：安装 client、启用 responder 角色、跑通 example hotline、新增自己的 Hotline draft、提交 Marketplace。所有命令真实可复制。",
      },
      {
        kind: "h2",
        text: "对 Agent 团队意味着什么",
      },
      {
        kind: "p",
        text: "Caller 端的故事同样关键。Agent 团队过去要为每根外部能力写一份 client、谈一份商务、跑一份对账。Hotline 让一份 client SDK 通吃 catalog 上任意 Hotline，所有调用共享 result_package，所有计费走协议。Agent 团队把「整合外部能力」从一个工程问题变成一个采购问题，把 Hotline 当 npm 包用。",
      },
      {
        kind: "h2",
        text: "我们为什么现在做这件事",
      },
      {
        kind: "p",
        text: "三件事一起到位：模型的工具调用能力开始稳定（Agent runtime 不再是玩具）；个人创作者经济在 AI 之前已经成熟（OPC 受众准备好了）；MCP / Skills 的兴起暴露了「连接是连接、商业是商业」这个边界——是时候补上协议级的商业层。CALL ANYTHING 的赌注是：未来 5 年最大的软件供给方不是公司，是一群挂着 Hotline 的超级个体。",
      },
    ],
    nextLinks: [
      { href: "/compare/hotline-vs-mcp/", label: "Hotline vs MCP：差异完整对比" },
      { href: "/docs/quick-start-responder", label: "5 分钟开通你的第一根 Hotline" },
      { href: "/glossary/", label: "查看完整术语表" },
    ],
  },
];

export function getBlogPostBySlug(slug: string): BlogPost | undefined {
  return BLOG_POSTS.find((post) => post.slug === slug);
}

/** Sort posts newest-first; used by /blog list and RSS feed. */
export function getOrderedBlogPosts(): BlogPost[] {
  return [...BLOG_POSTS].sort((a, b) => (a.date < b.date ? 1 : -1));
}
